import { faPeopleArrows } from '@fortawesome/pro-duotone-svg-icons';
import { Dictionary, groupBy } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { MemberType, ProjectId, Team, TeamMeta } from 'api/models';
import { EventSource, Project } from 'api/project';
import { EmptyState, EmptyStateIcon } from 'components/atoms/empty-state';
import { Loading } from 'components/atoms/loading';
import { Modal as ModalAtom, error } from 'components/atoms/modal';
import { Option } from 'components/atoms/select';
import { Extra } from 'components/molecules/squad-picker';
import { Option as SquadOption } from 'components/molecules/squad-picker/types';
import { useProjectSquad } from 'components/molecules/squad-picker/use-squad-options';
import { teamOption } from 'components/molecules/squad-picker/utils';
import { toastService } from 'components/molecules/toast';
import { translationKeyForAccessLevelLabel } from 'components/organisms/module-settings/partner/utils';
import { HelperText01, ProductiveHeading05 } from 'components/styles';
import { ProjectResource, useWithAccess } from 'hooks/rbac/useWithAccess';
import {
  ClickEventTags,
  useLoadLoggerForDataDog,
} from 'hooks/use-data-dog-logger';
import {
  useMemberInvitationConfirmation,
  filterNotInProjectUsers,
} from 'hooks/use-invite-user';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { getProjectMembersQueryKey } from 'queries';
import { ALL_PROJECTS_QUERY_KEY } from 'queries/constants';
import {
  PlaceholderResolutionsQueryKey,
  getPlaceholderResolutionByProjectQueryKeyV2,
  useMultiAssignPlaceholderMutation,
  usePlaceholdersWithMetaByProjectQuery,
} from 'queries/placeholder';
import {
  useAddProjectUsersLiteMutation,
  useRemoveProjectUsersLiteMutation,
} from 'queries/project';
import { sortByText } from 'utils/sort-by-text';
import { PlaceholderRow } from './placeholder-row';
import { usePlaceholderValueHelpers } from './placeholder-value-helper';
import { PlaceholderValueMap } from './types';

const MainHeader = styled.div`
  ${ProductiveHeading05};
`;
const Description = styled.div`
  ${HelperText01};
  color: var(--gray-80);
  margin-top: var(--spacing-02);
`;
const Title = () => (
  <>
    <MainHeader>
      <FormattedMessage id="projects.placeholder_resolution.modal.title" />
    </MainHeader>
    <Description>
      <FormattedMessage id="projects.placeholder_resolution.modal.title.description" />
    </Description>
  </>
);
const Wrapper = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  min-height: 200px;
  padding-bottom: var(--spacing-07);
`;
const StyledEmptyState = styled(EmptyState)`
  margin-left: var(--spacing-06);
  margin-top: var(--spacing-06);
  color: var(--gray-50);
  margin-bottom: var(--spacing-06);
  svg {
    width: 60px;
  }
  [class^='empty-state__Message'] {
    width: 100%;
  }
`;

const Modal = styled(ModalAtom)`
  .ant-modal-footer {
    padding: var(--spacing-05);
    border-top: 1px solid var(--border-color);
  }
`;

interface Props {
  placeholders: Team[];
  placeholderMeta: Dictionary<TeamMeta>;
  project: Project;
  placeholderValueMap: PlaceholderValueMap;
  onPlaceholderValueChange: (placeholder: Team, value?: Option<Extra>) => void;
  readOnly: boolean;
}

export const Content: FC<Props> = ({
  placeholders,
  project,
  placeholderValueMap,
  onPlaceholderValueChange,
  placeholderMeta,
  readOnly,
}) => {
  useLoadLoggerForDataDog([
    ClickEventTags.ProjectPlanPlaceholderResolutionPopup,
  ]);

  return (
    <Wrapper>
      {placeholders.length === 0 ? (
        <StyledEmptyState
          image={<EmptyStateIcon icon={faPeopleArrows} />}
          message={
            <FormattedMessage id="projects.create.team_details.placeholders.empty.message" />
          }
        />
      ) : (
        <>
          {placeholders.map((placeholder) => {
            return (
              <PlaceholderRow
                key={placeholder.teamId}
                placeholderMeta={placeholderMeta}
                placeholder={placeholder}
                project={project}
                value={placeholderValueMap[placeholder.teamId]}
                onChange={onPlaceholderValueChange}
                readOnly={readOnly}
              />
            );
          })}
        </>
      )}
    </Wrapper>
  );
};
const LoadingWrapper = styled.div`
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface ModalProps {
  onClose: () => void;
  visible: boolean;
  placeholders: Team[];
  project: Project;
}
export const PlaceholderResolutionModal: FC<ModalProps> = ({
  onClose,
  visible,
  placeholders,
  project,
}) => {
  const {
    getUserAssigned,
    getCompanyAssigned,
    getResponsibleCompanyForOption,
    doesCompanyHaveAcccesForPrivateTasks,
  } = usePlaceholderValueHelpers();

  const getValuesForPlaceholder = useCallback(
    (): PlaceholderValueMap =>
      placeholders.reduce((valueMap, placeholder) => {
        return {
          ...valueMap,
          [placeholder.teamId]:
            getUserAssigned(placeholder)?.option ??
            getCompanyAssigned(placeholder)?.option,
        };
      }, {}),
    [getCompanyAssigned, getUserAssigned, placeholders],
  );
  const [inProgress, setInProgress] = useState(false);
  const [placeholderValueMap, setPlaceholderValueMap] =
    useState<PlaceholderValueMap>(getValuesForPlaceholder());
  const translate = useTranslate();
  const { data: placeholderWithMeta, isFetching: placeholderMetaLoading } =
    usePlaceholdersWithMetaByProjectQuery(ProjectId(project.projectId), {
      refetchOnMount: 'always',
    });
  const placeholderMeta = useMemo(
    () =>
      (placeholderWithMeta ?? []).reduce(
        (metaMap, placeholder) => ({
          ...metaMap,
          [placeholder.teamId]: placeholder.meta,
        }),
        {},
      ),
    [placeholderWithMeta],
  );

  const onPlaceholderValueChange = useCallback(
    (placeholder: Team, selected?: Option<Extra>) => {
      if (
        (placeholderMeta[placeholder.teamId]?.privateTask ?? 0) !== 0 &&
        selected != null
      ) {
        const responsibleCompany = getResponsibleCompanyForOption(selected);

        if (responsibleCompany != null) {
          const access =
            doesCompanyHaveAcccesForPrivateTasks(responsibleCompany);

          if (!access.hasAccess) {
            const title = translate(
              'projects.placeholder_resolutions.cannot_assigned.private_tasks.title',
            );
            const content = translate(
              'projects.placeholder_resolutions.cannot_assigned.private_tasks.description',
              {
                placeholderName: placeholder.teamName,
                companyName: selected.label,
                accessLevel:
                  access.permissionSetName != null
                    ? translate(
                        translationKeyForAccessLevelLabel(
                          access.permissionSetName,
                        ),
                      )
                    : '',
                isUser: selected.extra?.type === 'user',
                numberOfTasks:
                  placeholderMeta[placeholder.teamId]?.privateTask ?? 0,
                strong: (chunks) => <strong>{chunks}</strong>,
              },
            );

            setPlaceholderValueMap((previous) => ({
              ...previous,
              [placeholder.teamId]: null,
            }));

            error({
              title,
              content,
            });

            return;
          }
        }
      }
      setPlaceholderValueMap((previous) => ({
        ...previous,
        [placeholder.teamId]: selected,
      }));
    },
    [
      doesCompanyHaveAcccesForPrivateTasks,
      getResponsibleCompanyForOption,
      placeholderMeta,
      translate,
    ],
  );
  const handleUserSelection = useMemberInvitationConfirmation(
    ProjectId(project.projectId),
  );
  const queryClient = useQueryClient();
  const { mutateAsync: addProjectUsersLite } = useAddProjectUsersLiteMutation();
  const { mutateAsync: removeProjectUserLite } =
    useRemoveProjectUsersLiteMutation();
  const successTitle = useTranslation(
    'projects.placeholder_resolution.success.title',
  );
  const successMessage = useTranslation(
    'projects.placeholder_resolution.success.message',
  );
  const errorTitle = useTranslation(
    'projects.placeholder_resolution.error.title',
  );
  const errorMessage = useTranslation(
    'projects.placeholder_resolution.error.message',
  );
  const { mutateAsync: multiAssignPlaceholder } =
    useMultiAssignPlaceholderMutation(project.projectId, {
      onSuccess: async () => {
        queryClient.invalidateQueries(
          getProjectMembersQueryKey(ProjectId(project.projectId)),
        );
        toastService({
          toastContainer: {},
          title: successTitle,
          message: successMessage,
        }).success();
      },
      onError: () => {
        toastService({
          toastContainer: {},
          title: errorTitle,
          message: errorMessage,
        }).error();
      },
    });

  const {
    data: { notInProject },
  } = useProjectSquad(ProjectId(project.projectId));

  const onSubmit = useCallback(async () => {
    const userOptions = (
      Object.values(placeholderValueMap).filter(Boolean) as Option<Extra>[]
    ).filter((option) => option.extra?.type === 'user');

    const ids = userOptions.map((item) => item?.extra?.id ?? 0);

    const result = filterNotInProjectUsers(ids, notInProject);

    const notInProjectUserIds = result.map(({ userId }) => userId);

    const confirmedUserOptions = await handleUserSelection(
      userOptions as SquadOption[],
    );

    if (confirmedUserOptions.length !== userOptions.length) {
      return;
    }
    const placeholdersWithNewlyAssignedUser = placeholders.filter(
      (placeholder) => {
        const newValueForPlaceholder = placeholderValueMap[placeholder.teamId];

        if (
          newValueForPlaceholder == null ||
          newValueForPlaceholder.extra?.type !== 'user'
        ) {
          return false;
        }

        return (
          newValueForPlaceholder.extra.id !== placeholder.userList?.[0]?.userId
        );
      },
    );

    const placeholdersWithRemovedUser = placeholders.filter((placeholder) => {
      const oldAssignedUserId = placeholder.userList?.[0]?.userId;

      if (oldAssignedUserId == null) {
        return false;
      }

      const isUserAssigned =
        placeholderValueMap[placeholder.teamId]?.extra?.type === 'user';

      return (
        !isUserAssigned ||
        placeholderValueMap[placeholder.teamId]?.extra?.id !== oldAssignedUserId
      );
    });

    setInProgress(true);
    try {
      if (placeholdersWithRemovedUser.length > 0) {
        await removeProjectUserLite({
          projectId: project.projectId,
          projectUsers: placeholdersWithRemovedUser
            .map((placeholder) => ({
              userId: placeholder.userList?.[0]?.userId as number,
              roleId: placeholder.roleId,
              referenceTeamId: placeholder.teamId,
            }))
            .filter(({ userId }) => userId != null),
          source: EventSource.PlaceholderManage,
        });
      }
      if (placeholdersWithNewlyAssignedUser.length > 0) {
        await addProjectUsersLite(
          {
            projectId: project.projectId,
            projectUsers: placeholdersWithNewlyAssignedUser.map(
              (placeholder) => ({
                userId:
                  placeholderValueMap[placeholder.teamId]?.extra?.id ?? -1,
                referenceTeamId: placeholder.teamId,
                roleId: placeholder.roleId,
              }),
            ),
            source: EventSource.PlaceholderManage,
            assignedToProjectMember: notInProjectUserIds?.length === 0,
          },
          {
            onSuccess: async () => {},
          },
        );
      }
      queryClient.invalidateQueries(ALL_PROJECTS_QUERY_KEY);
      queryClient.invalidateQueries(
        getProjectMembersQueryKey(ProjectId(project.projectId)),
      );
      const payload = placeholders.map((placeholder) => {
        return {
          option: {
            ...teamOption(placeholder),
            user: placeholderValueMap[placeholder.teamId],
          },
          roleId: placeholder.roleId,
        };
      });

      await multiAssignPlaceholder(payload);
      await queryClient.invalidateQueries(
        getPlaceholderResolutionByProjectQueryKeyV2(
          ProjectId(project.projectId),
        ),
      );
      setInProgress(false);
      onClose();
      queryClient.invalidateQueries(PlaceholderResolutionsQueryKey);
    } catch (e) {
      setInProgress(false);
      toastService({
        toastContainer: {},
        title: errorTitle,
        message: errorMessage,
      }).error();
    }
  }, [
    addProjectUsersLite,
    errorMessage,
    errorTitle,
    handleUserSelection,
    multiAssignPlaceholder,
    onClose,
    placeholderValueMap,
    placeholders,
    project.projectId,
    queryClient,
    removeProjectUserLite,
    notInProject,
  ]);

  const sortedPlaceholders = useMemo(() => {
    const grouped = groupBy(placeholders, 'teamType');

    return (grouped[MemberType.Native] ?? [])
      .sort((a, b) => sortByText(a.teamName, b.teamName))
      .concat(
        (grouped[MemberType.External] ?? []).sort((a, b) =>
          sortByText(a.teamName, b.teamName),
        ),
      );
  }, [placeholders]);
  const { hasAccessToResource } = useWithAccess();
  const hasTeamEditAccess = hasAccessToResource({
    rule: 'TASK_ASSIGNEE:WRITE',
    resourceData: ProjectResource(project),
  });

  return (
    <Modal
      title={<Title />}
      visible={visible}
      width={600}
      cancelButtonProps={{
        type: 'text',
      }}
      okButtonProps={{
        loading: inProgress,
        disabled: placeholderMetaLoading,
      }}
      okText={<FormattedMessage id="done" />}
      onCancel={onClose}
      onOk={onSubmit}
    >
      {placeholderMetaLoading ? (
        <LoadingWrapper>
          <Loading size="large" />
        </LoadingWrapper>
      ) : (
        <Content
          project={project}
          placeholders={sortedPlaceholders}
          placeholderMeta={placeholderMeta}
          placeholderValueMap={placeholderValueMap}
          onPlaceholderValueChange={onPlaceholderValueChange}
          readOnly={!hasTeamEditAccess}
        />
      )}
    </Modal>
  );
};
